<script setup lang="ts">
  import { computed, ref, type HTMLAttributes } from 'vue'
  import {
    IconError,
    IconWarning,
    IconInfo,
    IconChevronDown
  } from '@gat-ship/icons'
  import { cn } from '../../lib/utils'
  import type { FormFieldMessage } from './form-field-messages-types'

  /**
   * Renders a list of `FormFieldMessage`s using the same visual pattern as
   * V1's `gat-form-field-message.vue`: icon + short message on one line,
   * optional "show more / show less" toggle that reveals the `messageLong`
   * content (which can itself be a Vue component like a candidate list).
   *
   * Shows only the highest-severity bucket (error > warning > info) at a
   * time — matching V1 — so callers can push mixed variants without
   * worrying about crowding.
   */
  const props = defineProps<{
    messages: FormFieldMessage[]
    class?: HTMLAttributes['class']
  }>()

  const errorMessages = computed(() =>
    props.messages.filter((m) => m.variant === 'error')
  )
  const warningMessages = computed(() =>
    props.messages.filter((m) => m.variant === 'warning')
  )
  const infoMessages = computed(() =>
    props.messages.filter((m) => m.variant === 'info')
  )

  const shownMessages = computed(() => {
    if (errorMessages.value.length > 0) return errorMessages.value
    if (warningMessages.value.length > 0) return warningMessages.value
    if (infoMessages.value.length > 0) return infoMessages.value
    return []
  })

  const shownVariant = computed<'error' | 'warning' | 'info' | undefined>(
    () => shownMessages.value[0]?.variant
  )

  const longMessages = computed(() =>
    shownMessages.value.filter(
      (m) =>
        m.messageLong &&
        (typeof m.messageLong !== 'string' || m.messageLong.length > 0)
    )
  )

  const canShowMore = computed(() => longMessages.value.length > 0)
  const showMore = ref(false)
</script>

<template>
  <div
    v-if="shownMessages.length > 0"
    :class="
      cn(
        'flex w-full flex-col gap-2 pt-1',
        shownVariant === 'error' && 'text-destructive',
        shownVariant === 'warning' && 'text-amber-600 dark:text-amber-500',
        shownVariant === 'info' && 'text-muted-foreground',
        props.class
      )
    "
  >
    <div class="inline-flex w-full items-start gap-2 text-xs">
      <IconError v-if="shownVariant === 'error'" class="size-4 shrink-0" />
      <IconWarning
        v-else-if="shownVariant === 'warning'"
        class="size-4 shrink-0"
      />
      <IconInfo v-else class="size-4 shrink-0" />
      <span class="whitespace-pre-line">
        {{ shownMessages.map((m) => m.message).join('\n\n') }}
      </span>
      <button
        v-if="canShowMore"
        type="button"
        class="text-muted-foreground hover:text-foreground ml-auto inline-flex shrink-0 cursor-pointer items-center gap-0.5"
        @click.stop="showMore = !showMore"
      >
        <span class="text-xs">{{ showMore ? 'show less' : 'show more' }}</span>
        <icon-chevron-down
          class="size-4 transition-transform"
          :class="{ 'rotate-180': showMore }"
        />
      </button>
    </div>
    <div v-if="showMore" class="text-foreground">
      <template v-for="(msg, i) in longMessages" :key="i">
        <div
          v-if="typeof msg.messageLong === 'string'"
          class="whitespace-pre-line text-xs"
        >
          {{ msg.messageLong }}
        </div>
        <component :is="msg.messageLong" v-else />
      </template>
    </div>
  </div>
</template>
